import useSimStatuses from 'api/sims/useSimStatuses';
import useSimsByIdCall from 'api/sims/useSimsByIdCall';
import { Workbook } from 'exceljs';
import { DateTime } from 'luxon';
import { unparse } from 'papaparse';
import { useEffect, useRef, useState } from 'react';
import useAnalytics from 'state/contexts/Analytics/useAnalytics';
import useOrganization from 'state/contexts/Organization/useOrganization';
import type {
  EnumsSimNetworkState,
  EnumsSimStatus,
  SimsDaoSim,
} from 'types/__generated__/SimInventoryApi';
import {
  EnumSimInventoryExportType,
  exportTypeToLabel,
  type HologramSimIdsMap,
  type SimInventorySegmentName,
} from 'types/common';
import { downloadCSV } from 'utils/files';
import downloadJSON from 'utils/files/downloadJSON';
import downloadXLSX from 'utils/files/downloadXLSX';

interface UseExportSimsProps {
  sims: HologramSimIdsMap;
  exportType: EnumSimInventoryExportType;
  segmentName?: SimInventorySegmentName;
  isSuperAdmin: boolean;
}

const normalizeTableResults = (
  mergedResults: Array<SimsDaoSim>,
  formatStatus: (networkStatus: EnumsSimNetworkState | 'PENDING') => EnumsSimStatus,
  segmentName?: SimInventorySegmentName
) => {
  if (segmentName === 'active') {
    return mergedResults.map((sim) => ({
      orgId: sim.organizationId,
      orgName: sim.organizationName,
      hologramSimId: sim.hologramSimId,
      // eid: sim.eid,
      simName: sim.deviceName,
      deviceId: sim.deviceId,
      lastKnownImei: sim.imei,
      //   simEuiccState: sim.euiccState,
      simNetworkState: sim.networkState,
      simNetworkStatus: formatStatus(sim.networkState as EnumsSimNetworkState),
      enabledProfileIccid: sim.activeProfileIccid,
      enabledProfileName: sim.profileName,
      enabledProfileLinkId: sim.simProfileId,
      //   enabledProfileImsi: sim.imsi,
      // enabledProfileMsisdn: sim.msisdn,
      //   enabledProfileApn: sim.enabledProfileApn,
      // mostRecentNetwork: sim.mostRecentNetwork,
      // enabledProfileNetworkState: sim.enabledProfileNetworkState,
      //   mostRecentRat: sim.mostRecentRat,
      //   mostRecentSessionStartTime: sim.mostRecentSessionStartTime,
      mostRecentSessionEndTime: sim.mostRecentSession,
      //   fallbackState: sim.fallbackState,
      // fallbackTimer: sim.fallbackTimer,
      //   fallbackIsPrimaryProfile: sim.fallbackIsPrimaryProfile,
      dataPlanId: sim.planID,
      dataPlanName: sim.planName,
      //   dataPlanIncludedDataBytes: sim.dataPlanIncludedDataBytes,
      // dataPlanMonthlyCost: sim.dataPlanMonthlyCost,
      // dataPlanDataRate: sim.dataPlanDataRate,
      //   dataPlanSmsRate: sim.smsRate,
      //   dataPlanChargeOverageEnabled: sim.chargeOverage,
      //   dataUsageLimitBytes: sim.dataLimitBytes,
      dataUsageBytes: sim.billingPeriodUsage,
      //   outboundSms: sim.outboundSms,
      //   outboundSmsLimit: sim.outboundSmsLimit,
      // billingPeriodTotalCharges: sim.billingPeriodTotalCharges,
      //   billingPeriodRenewalDate: sim.billingRenewalDate,
      tagNames: sim.tags?.map((tag) => tag.name).join(','),
      tagIds: sim.tags?.map((tag) => tag.id).join(','),
      activationTime: sim.activationTime,
    }));
  }

  if (segmentName === 'preflight') {
    return mergedResults.map((sim) => ({
      orgId: sim.organizationId,
      orgName: sim.organizationName,
      hologramSimId: sim.hologramSimId,
      // eid: sim.eid,
      simName: sim.deviceName,
      deviceId: sim.deviceId,
      lastKnownImei: sim.imei,
      //   simEuiccState: sim.euiccState,
      simNetworkState: sim.networkState,
      simNetworkStatus: formatStatus(sim.networkState as EnumsSimNetworkState),
      enabledProfileIccid: sim.activeProfileIccid,
      enabledProfileName: sim.profileName,
      enabledProfileLinkId: sim.simProfileId,
      //   enabledProfileImsi: sim.imsi,
      // enabledProfileMsisdn: sim.msisdn,
      //   enabledProfileApn: sim.enabledProfileApn,
      // mostRecentNetwork: sim.mostRecentNetwork,
      // enabledProfileNetworkState: sim.enabledProfileNetworkState,
      //   mostRecentRat: sim.mostRecentRat,
      //   mostRecentSessionStartTime: sim.mostRecentSessionStartTime,
      mostRecentSessionEndTime: sim.mostRecentSession,
      //   fallbackState: sim.fallbackState,
      // fallbackTimer: sim.fallbackTimer,
      //   fallbackIsPrimaryProfile: sim.fallbackIsPrimaryProfile,
      dataPlanId: sim.planID,
      dataPlanName: sim.planName,
      //   dataPlanIncludedDataBytes: sim.dataPlanIncludedDataBytes,
      // dataPlanMonthlyCost: sim.dataPlanMonthlyCost,
      // dataPlanDataRate: sim.dataPlanDataRate,
      //   dataPlanSmsRate: sim.smsRate,
      //   dataPlanChargeOverageEnabled: sim.chargeOverage,
      //   dataUsageLimitBytes: sim.dataLimitBytes,
      dataUsageBytes: sim.billingPeriodUsage,
      //   outboundSms: sim.outboundSms,
      //   outboundSmsLimit: sim.outboundSmsLimit,
      // billingPeriodTotalCharges: sim.billingPeriodTotalCharges,
      //   billingPeriodRenewalDate: sim.billingRenewalDate,
      tagNames: sim.tags?.map((tag) => tag.name).join(','),
      tagIds: sim.tags?.map((tag) => tag.id).join(','),
      preflightDurationDays: sim.preflightDuration,
      preflightExitBehavior: sim.preflightExitBehavior,
      preflightStartTime: sim.preflightStartTime,
      preflightUsageBytes: sim.preflightUsage,
      expectedActivationTime: sim.expectedActivationTime,
      expectedPreflightDuration: sim.expectedPreflightDuration,
      activationTime: sim.activationTime,
    }));
  }

  if (segmentName === 'deactivated') {
    return mergedResults.map((sim) => ({
      orgId: sim.organizationId,
      orgName: sim.organizationName,
      hologramSimId: sim.hologramSimId,
      // eid: sim.eid,
      simName: sim.deviceName,
      deviceId: sim.deviceId,
      lastKnownImei: sim.imei,
      //   simEuiccState: sim.euiccState,
      simNetworkState: sim.networkState,
      simNetworkStatus: formatStatus(sim.networkState as EnumsSimNetworkState),
      enabledProfileIccid: sim.activeProfileIccid,
      enabledProfileName: sim.profileName,
      enabledProfileLinkId: sim.simProfileId,
      //   enabledProfileImsi: sim.imsi,
      // enabledProfileMsisdn: sim.msisdn,
      //   enabledProfileApn: sim.enabledProfileApn,
      // mostRecentNetwork: sim.mostRecentNetwork,
      // enabledProfileNetworkState: sim.enabledProfileNetworkState,
      //   mostRecentRat: sim.mostRecentRat,
      //   mostRecentSessionStartTime: sim.mostRecentSessionStartTime,
      mostRecentSessionEndTime: sim.mostRecentSession,
      //   fallbackState: sim.fallbackState,
      // fallbackTimer: sim.fallbackTimer,
      //   fallbackIsPrimaryProfile: sim.fallbackIsPrimaryProfile,
      dataPlanId: sim.planID,
      dataPlanName: sim.planName,
      //   dataPlanIncludedDataBytes: sim.dataPlanIncludedDataBytes,
      // dataPlanMonthlyCost: sim.dataPlanMonthlyCost,
      // dataPlanDataRate: sim.dataPlanDataRate,
      //   dataPlanSmsRate: sim.smsRate,
      //   dataPlanChargeOverageEnabled: sim.chargeOverage,
      //   dataUsageLimitBytes: sim.dataLimitBytes,
      dataUsageBytes: sim.billingPeriodUsage,
      //   outboundSms: sim.outboundSms,
      //   outboundSmsLimit: sim.outboundSmsLimit,
      // billingPeriodTotalCharges: sim.billingPeriodTotalCharges,
      //   billingPeriodRenewalDate: sim.billingRenewalDate,
      tagNames: sim.tags?.map((tag) => tag.name).join(','),
      tagIds: sim.tags?.map((tag) => tag.id).join(','),
      activationTime: sim.activationTime,
      deactivationTime: sim.deactivationTime,
    }));
  }

  return mergedResults.map((sim) => ({
    orgId: sim.organizationId,
    orgName: sim.organizationName,
    hologramSimId: sim.hologramSimId,
    // eid: sim.eid,
    simName: sim.deviceName,
    deviceId: sim.deviceId,
    lastKnownImei: sim.imei,
    //   simEuiccState: sim.euiccState,
    simNetworkState: sim.networkState,
    simNetworkStatus: formatStatus(sim.networkState as EnumsSimNetworkState),
    enabledProfileIccid: sim.activeProfileIccid,
    enabledProfileName: sim.profileName,
    enabledProfileLinkId: sim.simProfileId,
    //   enabledProfileImsi: sim.imsi,
    // enabledProfileMsisdn: sim.msisdn,
    //   enabledProfileApn: sim.enabledProfileApn,
    // mostRecentNetwork: sim.mostRecentNetwork,
    // enabledProfileNetworkState: sim.enabledProfileNetworkState,
    //   mostRecentRat: sim.mostRecentRat,
    //   mostRecentSessionStartTime: sim.mostRecentSessionStartTime,
    mostRecentSessionEndTime: sim.mostRecentSession,
    //   fallbackState: sim.fallbackState,
    // fallbackTimer: sim.fallbackTimer,
    //   fallbackIsPrimaryProfile: sim.fallbackIsPrimaryProfile,
    dataPlanId: sim.planID,
    dataPlanName: sim.planName,
    //   dataPlanIncludedDataBytes: sim.dataPlanIncludedDataBytes,
    // dataPlanMonthlyCost: sim.dataPlanMonthlyCost,
    // dataPlanDataRate: sim.dataPlanDataRate,
    //   dataPlanSmsRate: sim.smsRate,
    //   dataPlanChargeOverageEnabled: sim.chargeOverage,
    //   dataUsageLimitBytes: sim.dataLimitBytes,
    dataUsageBytes: sim.billingPeriodUsage,
    //   outboundSms: sim.outboundSms,
    //   outboundSmsLimit: sim.outboundSmsLimit,
    // billingPeriodTotalCharges: sim.billingPeriodTotalCharges,
    //   billingPeriodRenewalDate: sim.billingRenewalDate,
    tagNames: sim.tags?.map((tag) => tag.name).join(','),
    tagIds: sim.tags?.map((tag) => tag.id).join(','),
    preflightDurationDays: sim.preflightDuration,
    preflightExitBehavior: sim.preflightExitBehavior,
    preflightStartTime: sim.preflightStartTime,
    preflightUsageBytes: sim.preflightUsage,
    expectedActivationTime: sim.expectedActivationTime,
    expectedPreflightDuration: sim.expectedPreflightDuration,
    activationTime: sim.activationTime,
    deactivationTime: sim.deactivationTime,
  }));
};

const normalizeJsonResults = (
  mergedResults: Array<SimsDaoSim>,
  formatStatus: (networkStatus: EnumsSimNetworkState | 'PENDING') => EnumsSimStatus,
  segmentName?: SimInventorySegmentName
) => {
  if (segmentName === 'active') {
    return mergedResults.map((sim) => ({
      hologramSimId: sim.hologramSimId,
      name: sim.deviceName,
      //   eid: sim.eid,
      enabledProfileIccid: sim.activeProfileIccid,
      networkStatus: formatStatus(sim.networkState as EnumsSimNetworkState),
      networkState: sim.networkState,
      // sku: sim.sku,
      // printedSku: sim.printedSku,
      dataUsageBytes: sim.billingPeriodUsage,
      // dataLimitBytes: sim.dataLimitBytes,
      // outboundSms: sim.outboundSms,
      // outboundSmsLimit: sim.outboundSmsLimit,
      organization: {
        orgId: sim.organizationId,
        orgName: sim.organizationName,
      },
      device: {
        deviceId: sim.deviceId,
        lastKnownImei: sim.imei,
      },
      dataPlan: {
        dataPlanId: sim.planID,
        name: sim.planName,
        // platformFee: sim.platformFee,
        //  includedDataBytes: sim.includedDataBytes,
        // dataUsageRate: sim.dataUsageRate,
        // smsRate: sim.smsRate,
      },
      //   profiles: [],
      mostRecentSession: {
        // startTime: sim.mostRecentSession,
        endTime: sim.mostRecentSession,
        // dataUsageBytes: sim.mostRecentSessionBytes,
        // network: 'T-Mobile',
        // cellTowerId: '12345',
        // approximateLatLong: '50.0000000000,-50.00000000000',
      },
      //  networkFallback: {
      // status: sim.fallbackStatus,
      // fallbackProfileLinkId: sim.fallbackProfileLinkId,
      // fallbackTimerSeconds: sim.fallbackTimerSeconds,
      //  },
      tags: sim.tags,
      activationTime: sim.activationTime,
      // customFields: sim.customFields,
    }));
  }

  if (segmentName === 'preflight') {
    return mergedResults.map((sim) => ({
      hologramSimId: sim.hologramSimId,
      name: sim.deviceName,
      //   eid: sim.eid,
      enabledProfileIccid: sim.activeProfileIccid,
      networkStatus: formatStatus(sim.networkState as EnumsSimNetworkState),
      networkState: sim.networkState,
      // sku: sim.sku,
      // printedSku: sim.printedSku,
      dataUsageBytes: sim.billingPeriodUsage,
      // dataLimitBytes: sim.dataLimitBytes,
      // outboundSms: sim.outboundSms,
      // outboundSmsLimit: sim.outboundSmsLimit,
      organization: {
        orgId: sim.organizationId,
        orgName: sim.organizationName,
      },
      device: {
        deviceId: sim.deviceId,
        lastKnownImei: sim.imei,
      },
      dataPlan: {
        dataPlanId: sim.planID,
        name: sim.planName,
        // platformFee: sim.platformFee,
        //  includedDataBytes: sim.includedDataBytes,
        // dataUsageRate: sim.dataUsageRate,
        // smsRate: sim.smsRate,
      },
      //   profiles: [],
      mostRecentSession: {
        // startTime: sim.mostRecentSession,
        endTime: sim.mostRecentSession,
        // dataUsageBytes: sim.mostRecentSessionBytes,
        // network: 'T-Mobile',
        // cellTowerId: '12345',
        // approximateLatLong: '50.0000000000,-50.00000000000',
      },
      //  networkFallback: {
      // status: sim.fallbackStatus,
      // fallbackProfileLinkId: sim.fallbackProfileLinkId,
      // fallbackTimerSeconds: sim.fallbackTimerSeconds,
      //  },
      tags: sim.tags,
      preflight: {
        exitBehavior: sim.preflightExitBehavior,
        startTime: sim.preflightStartTime,
        //  expectedEndTime: sim.preflightExpectedEndTime,
        //  endTime: sim.preflightEndTime,
        expectedDurationDays: sim.expectedPreflightDuration,
        durationDays: sim.preflightDuration,
        //  dataLimitBytes: sim.preflightDataLimitBytes,
        usageBytes: sim.preflightUsage,
        // outboundSmsLimit: sim.preflightOutboundSmsLimit,
      },
      activationTime: sim.activationTime,
      // customFields: sim.customFields,
    }));
  }

  if (segmentName === 'deactivated') {
    return mergedResults.map((sim) => ({
      hologramSimId: sim.hologramSimId,
      name: sim.deviceName,
      //   eid: sim.eid,
      enabledProfileIccid: sim.activeProfileIccid,
      networkStatus: formatStatus(sim.networkState as EnumsSimNetworkState),
      networkState: sim.networkState,
      // sku: sim.sku,
      // printedSku: sim.printedSku,
      dataUsageBytes: sim.billingPeriodUsage,
      // dataLimitBytes: sim.dataLimitBytes,
      // outboundSms: sim.outboundSms,
      // outboundSmsLimit: sim.outboundSmsLimit,
      organization: {
        orgId: sim.organizationId,
        orgName: sim.organizationName,
      },
      device: {
        deviceId: sim.deviceId,
        lastKnownImei: sim.imei,
      },
      dataPlan: {
        dataPlanId: sim.planID,
        name: sim.planName,
        // platformFee: sim.platformFee,
        //  includedDataBytes: sim.includedDataBytes,
        // dataUsageRate: sim.dataUsageRate,
        // smsRate: sim.smsRate,
      },
      //   profiles: [],
      mostRecentSession: {
        // startTime: sim.mostRecentSession,
        endTime: sim.mostRecentSession,
        // dataUsageBytes: sim.mostRecentSessionBytes,
        // network: 'T-Mobile',
        // cellTowerId: '12345',
        // approximateLatLong: '50.0000000000,-50.00000000000',
      },
      //  networkFallback: {
      // status: sim.fallbackStatus,
      // fallbackProfileLinkId: sim.fallbackProfileLinkId,
      // fallbackTimerSeconds: sim.fallbackTimerSeconds,
      //  },
      tags: sim.tags,
      activationTime: sim.activationTime,
      deactivationTime: sim.deactivationTime,
      // customFields: sim.customFields,
    }));
  }

  return mergedResults.map((sim) => ({
    hologramSimId: sim.hologramSimId,
    name: sim.deviceName,
    //   eid: sim.eid,
    enabledProfileIccid: sim.activeProfileIccid,
    networkStatus: formatStatus(sim.networkState as EnumsSimNetworkState),
    networkState: sim.networkState,
    // sku: sim.sku,
    // printedSku: sim.printedSku,
    dataUsageBytes: sim.billingPeriodUsage,
    // dataLimitBytes: sim.dataLimitBytes,
    // outboundSms: sim.outboundSms,
    // outboundSmsLimit: sim.outboundSmsLimit,
    organization: {
      orgId: sim.organizationId,
      orgName: sim.organizationName,
    },
    device: {
      deviceId: sim.deviceId,
      lastKnownImei: sim.imei,
    },
    dataPlan: {
      dataPlanId: sim.planID,
      name: sim.planName,
      // platformFee: sim.platformFee,
      //  includedDataBytes: sim.includedDataBytes,
      // dataUsageRate: sim.dataUsageRate,
      // smsRate: sim.smsRate,
    },
    //   profiles: [],
    mostRecentSession: {
      // startTime: sim.mostRecentSession,
      endTime: sim.mostRecentSession,
      // dataUsageBytes: sim.mostRecentSessionBytes,
      // network: 'T-Mobile',
      // cellTowerId: '12345',
      // approximateLatLong: '50.0000000000,-50.00000000000',
    },
    //  networkFallback: {
    // status: sim.fallbackStatus,
    // fallbackProfileLinkId: sim.fallbackProfileLinkId,
    // fallbackTimerSeconds: sim.fallbackTimerSeconds,
    //  },
    tags: sim.tags,
    preflight: {
      exitBehavior: sim.preflightExitBehavior,
      startTime: sim.preflightStartTime,
      //  expectedEndTime: sim.preflightExpectedEndTime,
      //  endTime: sim.preflightEndTime,
      expectedDurationDays: sim.expectedPreflightDuration,
      durationDays: sim.preflightDuration,
      //  dataLimitBytes: sim.preflightDataLimitBytes,
      usageBytes: sim.preflightUsage,
      // outboundSmsLimit: sim.preflightOutboundSmsLimit,
    },
    activationTime: sim.activationTime,
    deactivationTime: sim.deactivationTime,
    // customFields: sim.customFields,
  }));
};

const useExportSims = ({ sims, exportType, segmentName, isSuperAdmin }: UseExportSimsProps) => {
  const [loadingSims, setLoadingSims] = useState(true);
  const [processingExport, setProcessingExport] = useState(false);
  const [error, setError] = useState<any | null>(null);
  const { sendAnalyticsEvent, sendAnalyticsError } = useAnalytics();

  const { isLoading: simStatusesLoading, formatStatus, error: simsStatusError } = useSimStatuses();

  // Necessary in Dev mode. Nice to have in Prod just in case.
  const exportedSims = useRef<boolean>(false);
  const organizationId = useOrganization();

  const getSimsById = useSimsByIdCall({ sims, organizationId, isSuperAdmin });
  const exportTypeLabel = exportTypeToLabel(exportType);

  useEffect(() => {
    const handleExport = async () => {
      const start = performance.now();
      let gotSimsIn: number | undefined;
      let mergedSimsIn: number | undefined;
      let normalizedResultsIn: number | undefined;
      let wroteXlsxToBufferIn: number | undefined;
      let createdSheetIn: number | undefined;
      let downloadedIn: number | undefined;

      try {
        const exportResults = await getSimsById();
        gotSimsIn = performance.now() - start;

        if (!exportResults) {
          setLoadingSims(false);
          setError(new Error('No SIMs found'));
          return;
        }

        setLoadingSims(false);
        setProcessingExport(true);

        const mergedResults = exportResults?.reduce<Array<SimsDaoSim>>(
          (acc, result) => (result.sims ? acc.concat(result.sims) : acc),
          []
        );
        mergedSimsIn = performance.now() - start;

        let normalizedResults;

        const now = DateTime.now();
        const timestamp = now.toFormat('yyyyLLdd-hhmmssSSS');

        switch (exportType) {
          case EnumSimInventoryExportType.XLSX:
          case EnumSimInventoryExportType.CSV:
            // XLSX/CSV share the same mapping function
            normalizedResults = normalizeTableResults(mergedResults, formatStatus, segmentName);

            normalizedResultsIn = performance.now() - start;

            if (exportType === EnumSimInventoryExportType.XLSX) {
              const workbook = new Workbook();
              workbook.creator = 'Hologram';
              workbook.lastModifiedBy = 'Hologram';

              const nowJS = now.toJSDate();
              workbook.created = nowJS;
              workbook.modified = nowJS;

              const sheet = workbook.addWorksheet('SIMs');
              sheet.addRow(Object.keys(normalizedResults[0]));
              sheet.addRows(normalizedResults.map((sim) => Object.values(sim)));
              createdSheetIn = performance.now() - start;

              const buffer = await workbook.xlsx.writeBuffer();
              wroteXlsxToBufferIn = performance.now() - start;

              downloadXLSX({
                data: buffer as any,
                filename: `SIMs-${timestamp}.xlsx`,
              });
              downloadedIn = performance.now() - start;
            } else {
              downloadCSV({ data: unparse(normalizedResults), filename: `SIMs-${timestamp}.csv` });
              downloadedIn = performance.now() - start;
            }

            break;
          case EnumSimInventoryExportType.JSON:
            normalizedResults = normalizeJsonResults(mergedResults, formatStatus, segmentName);

            normalizedResultsIn = performance.now() - start;
            downloadJSON({
              data: JSON.stringify(normalizedResults, undefined, 1),
              filename: `SIMs-${timestamp}.json`,
            });
            downloadedIn = performance.now() - start;
            break;
          default:
            break;
        }

        setProcessingExport(false);
      } catch (e) {
        if (sendAnalyticsError) {
          sendAnalyticsError('Export SIMs error', e as any);
        }
        setError(e);
      }

      if (sendAnalyticsEvent) {
        sendAnalyticsEvent(`SIMs - ${segmentName} - Export SIMs - Export SIMs success`, {
          exportType: exportTypeLabel,
          downloadedIn,
          gotSimsIn,
          mergedSimsIn,
          normalizedResultsIn,
          wroteXlsxToBufferIn,
          createdSheetIn,
        });
      }

      setLoadingSims(false);
    };

    if (simsStatusError) {
      setError(simsStatusError);
      return;
    }

    if (!exportedSims.current && !simStatusesLoading) {
      exportedSims.current = true;
      handleExport();
    }
  }, [
    error,
    exportType,
    exportTypeLabel,
    formatStatus,
    getSimsById,
    segmentName,
    sendAnalyticsError,
    sendAnalyticsEvent,
    simStatusesLoading,
    simsStatusError,
  ]);

  return { loadingSims, processingExport, error };
};

export default useExportSims;
